"use client";

import { useState, useRef, useEffect } from "react";
import { apiRequest } from "@/lib/api/_fetch";
import type { PlaceResult } from "@/lib/api/misc";
import {
  LOADING_STEPS,
  STEP_DELAY_MS,
  type AshtakvargaRequest,
  type AshtakvargaResponse,
} from "./_components/AshtakvargaTypes";
import { AshtakvargaForm } from "./_components/AshtakvargaForm";
import { SAVTable, BAVTable, HousePredictions } from "./_components/AshtakvargaResults";

export default function AshtakvargaClient() {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AshtakvargaResponse | null>(null);

  const resultRef = useRef<HTMLDivElement>(null);

  // Scroll to result when it arrives
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  // Animate loading steps
  useEffect(() => {
    if (!loading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoadingStep(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, STEP_DELAY_MS);
    return () => clearInterval(interval);
  }, [loading]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!birthPlace) {
      setError("Please enter your birth place.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const body: AshtakvargaRequest = {
        name: name.trim() || "Anonymous",
        birth_date: birthDate,
        birth_time: birthTime || null,
        birth_place: birthPlace,
        latitude: selectedPlace?.latitude ?? null,
        longitude: selectedPlace?.longitude ?? null,
        timezone: selectedPlace?.timezone ?? null,
      };
      const data = await apiRequest<AshtakvargaResponse>("/ashtakvarga", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="card-sacred p-6 sm:p-8 mb-8"
      style={{ borderTop: "3px solid var(--ish-accent)" }}
    >
      <h2
        className="text-[20px] font-bold mb-1"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Free Ashtakvarga Calculator
      </h2>
      <p className="text-body-small mb-6" style={{ color: "var(--ish-text-muted)" }}>
        Enter your birth details to get your complete BAV + SAV chart — seven Bhinnashtakvarga
        tables, Sarvashtakvarga totals, and per-house predictions.
      </p>

      <AshtakvargaForm
        name={name}
        setName={setName}
        birthDate={birthDate}
        setBirthDate={setBirthDate}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        birthPlace={birthPlace}
        setBirthPlace={setBirthPlace}
        setSelectedPlace={(p) => setSelectedPlace(p)}
        loading={loading}
        loadingStep={loadingStep}
        error={error}
        onSubmit={handleSubmit}
      />

      {/* Results */}
      {result && (
        <div ref={resultRef} className="mt-8 space-y-6">
          <SAVTable result={result} />
          <BAVTable result={result} />
          <HousePredictions result={result} />
        </div>
      )}
    </div>
  );
}
